import { applyEventApiError } from './applyEventApiError'
import type { EventFormValues, SnEvent } from './types'
import type { EventSeriesScope } from './seriesScope'

/** Shared save toast copy, so web and mobile show identical wording. Delete
 *  has no toast — navigating away from the deleted event is confirmation
 *  enough. */
export const EVENT_CREATED_MESSAGE = 'New Event Has Been Created'
export const EVENT_UPDATED_MESSAGE = 'The Event Has Been Updated'

/**
 * Structural subset of Formik's `FormikHelpers` this module needs. Typed
 * locally rather than importing `formik` — `common/` stays framework-agnostic
 * and this is the entire surface `submitEvent` touches, so any object with a
 * matching shape (Formik's real helpers, or a mobile equivalent) works.
 */
export interface EventSubmitHelpers {
  setErrors: (errors: Record<string, string>) => void
  setTouched: (
    touched: Record<string, boolean>,
    shouldValidate?: boolean
  ) => void
}

export interface EventSubmitDeps {
  /** Null for add mode; the event being edited otherwise. */
  event: SnEvent | null
  /** Absent for a schedule-owned event, which belongs to no team. */
  teamId?: string
  /** Required to create a team event; not needed otherwise. */
  rosterId?: string
  /** Set instead of `teamId`/`rosterId` for a schedule-owned event. */
  scheduleId?: string
  createEvent: (values: EventFormValues) => Promise<SnEvent>
  updateEvent: (
    eventId: string,
    values: EventFormValues,
    scope?: EventSeriesScope
  ) => Promise<SnEvent>
  /** Surface the success toast (web: message.success). */
  notifySuccess: (message: string) => void
  /** Surface the error toast (web: message.error). */
  notifyError: (message: string) => void
  /**
   * Fired only after a successful save, with the event the API returned so
   * callers can react to WHICH event changed — the schedule list uses its
   * `starts_at` to follow it to the right tab.
   */
  onSaved?: (saved: SnEvent) => void
  /**
   * Receives the errored form-field keys after a rejected save, so the caller
   * can bring the first one into view (mobile scrolls to it; web's forms
   * already do the same via applyEventApiError). Field errors render inline,
   * which is invisible if the field sits off-screen.
   */
  onFieldErrors?: (fields: string[]) => void
}

/**
 * Create-or-update orchestration for the event form: calls the right
 * mutation, shows the matching success copy, fires `onSaved`, and routes any
 * thrown API error through `applyEventApiError` on failure.
 *
 * `teamId`/`rosterId`/`scheduleId` aren't used directly here —
 * `createEvent`/`updateEvent` are already scoped to them by the caller (see
 * `useEventMutations`) — but they're kept on `EventSubmitDeps` so a caller's
 * intent (which team/roster or which schedule this submit belongs to) is
 * explicit and typed at the call site.
 */
export const submitEvent = async (
  values: EventFormValues,
  helpers: EventSubmitHelpers,
  deps: EventSubmitDeps
): Promise<void> => {
  const {
    event,
    createEvent,
    updateEvent,
    notifySuccess,
    notifyError,
    onSaved,
    onFieldErrors,
  } = deps

  try {
    let saved: SnEvent
    if (event) {
      // Scope rides the query string, not the payload; a one-off event leaves
      // `editScope` unset so the request is unchanged.
      saved = await updateEvent(event.id, values, values.editScope)
      notifySuccess(EVENT_UPDATED_MESSAGE)
    } else {
      saved = await createEvent(values)
      notifySuccess(EVENT_CREATED_MESSAGE)
    }
    onSaved?.(saved)
  } catch (err) {
    applyEventApiError(err, {
      setErrors: helpers.setErrors,
      setTouched: (touched) => helpers.setTouched(touched, false),
      notifyError,
      onFieldErrors,
    })
  }
}
